import {AXON_EVENT_KEY} from '@/shared/config';

const AXON_SCRIPT_SOURCES = [
  'https://s.axon.ai/pixel.js',
  'https://res4.applovin.com/p/l/loader.iife.js',
] as const;

let initialized = false;

const isAvailableGlobalInstance = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return !(typeof window.axon === 'undefined' || window.axon == null);
};

const createProxyInstance = (eventKey: string) => {
  if (isAvailableGlobalInstance()) {
    return;
  }

  const proxy: AxonFunction = (...args) => {
    if (typeof proxy.performOperation === 'function') {
      proxy.performOperation(...args);
      return;
    }

    if (!proxy.operationQueue) {
      proxy.operationQueue = [];
    }

    proxy.operationQueue.push(args);
  };

  proxy.operationQueue = [];
  proxy.ts = Date.now();
  proxy.eventKey = eventKey;

  window.axon = proxy;
};

const injectScript = (src: string) => {
  if (document.querySelector(`script[src="${src}"]`)) {
    return;
  }

  const firstScript = document.getElementsByTagName('script')[0];

  if (!firstScript?.parentNode) {
    return;
  }

  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  firstScript.parentNode.insertBefore(script, firstScript);
};

export function initPixel(): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (initialized) {
    return;
  }

  if (!AXON_EVENT_KEY) {
    return;
  }

  if (!isAvailableGlobalInstance()) {
    createProxyInstance(AXON_EVENT_KEY);

    AXON_SCRIPT_SOURCES.forEach((src) => {
      injectScript(src);
    });
  }

  try {
    window.axon?.('init');
    initialized = true;
  } catch (error) {
    console.warn('Error initializing Axon Pixel:', error);
  }
}

export function logEvent(
  eventName: string,
  eventData?: AxonEventData,
  options?: AxonTrackOptions
): void {
  if (typeof window === 'undefined') {
    return;
  }

  if (!isAvailableGlobalInstance()) {
    return;
  }

  try {
    window.axon?.('track', eventName, eventData, options);
  } catch (error) {
    console.warn('Error logging Axon event:', error);
  }
}
